import {Controller} from 'react-hook-form';

import Typography from 'components/Typography';
import PageHeader from 'components/PageHeader';
import Button from 'components/Button';
import FormActionGroup from 'components/FormActionGroup';
import FormGroup from 'components/FormGroup';
import TextField from 'components/TextField';
import SelectField from 'components/Select';
import ListWrapper from 'components/ListWrapper';
import CardContent from 'components/CardContent';

import {validateEmail} from 'utils/validators';

import useEmployeeForm from './useEmployeeForm';

import styles from './EmployeeForm.module.scss';
import roles from 'constants/roles';
import {SetStateAction, useMemo, useState} from 'react';
import EyeIcon from './icons/eye.svg';
import EyeDisabledIcon from './icons/eye-disable.svg';
import Modal from 'components/Modal';
import VehicleForm from 'pages/VehicleForm';
import ReactSwitch from 'react-switch';
import DeleteModal from 'components/DeleteModal';
import infoIcon from './icons/info.svg';
import Loader from 'components/DataTable/components/Loader';
import {phoneFormat} from 'helpers/phoneFormat';
import {get} from 'lodash';
import Checkbox from 'components/Checkbox';
import CreatableSelect from 'components/CreatableSelect';
import useCompanyConfig from 'hooks/useCompanyConfig';

type Props = {
  isModal?: boolean;
  setUser?: (data: any) => void;
  setModalVisible?: (data: SetStateAction<boolean>) => void;
  sharedDefaultValue?: string;
};

function EmployeeForm({sharedDefaultValue, isModal, setUser}: Props) {
  const {
    role,
    teamId,
    team,
    isStaff,
    employeeId,
    submitting,
    errors,
    control,
    register,
    handleSubmit,
    setTeam,
    watch,
    setValue,
    loading,
    handleSendAppLink,
    handleDeleteEmployee,
    isDeleting,
    employee,
    currentUser,
    permissions,
    onChangePermission,
    userPermissions,
    fieldRoleOptions,
    creatingFieldRole,
    onCreateFieldRoleOption,
    canDelete,
    canUpdate,
  } = useEmployeeForm(!!isModal, setUser, sharedDefaultValue);
  const [deleteModal, setDeleteModal] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const {isMSPProvider, isPropertyManager} = useCompanyConfig();

  const staffTypeOptions = useMemo(() => {
    const propertyManagerOptions = roles;
    const mspOptions = propertyManagerOptions.map(
      (option: (typeof propertyManagerOptions)[0]) => ({
        ...option,
        label: option.value === 'landlord' ? 'Owner' : option.label,
      })
    );
    const defaultOptions = propertyManagerOptions.filter(
      (item) => item.value !== 'landlord'
    );
    return isMSPProvider
      ? mspOptions
      : isPropertyManager
      ? propertyManagerOptions
      : defaultOptions;
  }, [isMSPProvider, isPropertyManager]);
  return (
    <div style={{position: 'relative'}}>
      {loading && (
        <div className={styles.loadingWrapper}>
          <Loader />
        </div>
      )}
      <PageHeader>
        <div className={styles.headerLeft}>
          <Typography variant="h2">
            {employeeId
              ? `${isStaff ? 'Staff' : 'Onsite Worker'}: ${get(
                  employee,
                  'first_name',
                  ''
                )} ${get(employee, 'last_name', '')}`
              : isStaff
              ? 'New Staff'
              : `${
                  team
                    ? `New Onsite Worker for ${team.name}`
                    : 'New Onsite Worker'
                }`}
          </Typography>
          <div className={styles.switchCon}>
            <div className={styles.tooltipContainer}>
              <img className={styles.infoIcon} src={infoIcon} alt="info" />
              <div className={styles.tooltip}>
                Employees that are active can login, receive jobs and will be
                billed as paid users. Employees that are NOT active can NOT
                login or receive jobs and will not be billed as paid users.{' '}
              </div>
            </div>
            <p style={{marginRight: 7}}>Active:</p>
            <label>
              <ReactSwitch
                onColor="#4285f4"
                onChange={(val) => setValue('is_active', val)}
                handleDiameter={22}
                height={26}
                width={45}
                checked={watch('is_active')}
              />
            </label>
          </div>
        </div>
        {!isModal && (
          <FormActionGroup>
            {!!employeeId && role === 'driver' && (
              <Button onClick={handleSendAppLink} color="primary">
                Send App Link
              </Button>
            )}
            {!!employeeId && String(currentUser?.id) !== employeeId && (
              <Button
                onClick={() => setDeleteModal(true)}
                color="danger"
                loading={isDeleting}
              >
                Delete
              </Button>
            )}

            <Button onClick={handleSubmit} loading={submitting}>
              Save
            </Button>
          </FormActionGroup>
        )}
      </PageHeader>
      <ListWrapper style={{flex: 1}}>
        <CardContent px="md" pt="none" pb="md">
          <Typography style={{marginTop: 20}} variant="h2">
            Account Details
          </Typography>
          <FormGroup columns={2}>
            <TextField
              label="First Name"
              placeholder="First Name"
              {...register('first_name', {required: 'Required'})}
              error={errors.first_name?.message}
            />
            <TextField
              label="Last Name"
              placeholder="Last Name"
              {...register('last_name', {required: 'Required'})}
              error={errors.last_name?.message}
            />
          </FormGroup>

          <FormGroup columns={2}>
            <TextField
              label="Email Address"
              placeholder="Email Address"
              {...register('email', {
                required: 'Required',
                validate: validateEmail,
              })}
              error={errors.email?.message}
            />
            <TextField
              label="Phone Number"
              placeholder="(123) 456-7890"
              {...register('phone')}
              type="tel"
              minLength={14}
              onChange={(e) => {
                let input = e.target.value.replace(/\D/g, ''); // present only numbers

                e.target.value = phoneFormat(input);
              }}
              error={errors.phone?.message}
            />
          </FormGroup>
          <FormGroup columns={2}>
            <div style={{position: 'relative'}}>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                {...register(
                  'password',
                  !employeeId
                    ? {
                        required: 'Required',
                      }
                    : {}
                )}
                error={errors.password?.message}
              />
              <img
                onClick={() => setShowPassword((pr) => !pr)}
                className={styles.eyeIcon}
                src={showPassword ? EyeIcon : EyeDisabledIcon}
                alt="eye"
              />
            </div>
            <div style={{position: 'relative'}}>
              <TextField
                label="Password Confirmation"
                type={showPassword1 ? 'text' : 'password'}
                placeholder="Password Confirmation"
                {...register(
                  'password_confirmation',
                  !employeeId
                    ? {
                        required: 'Required',
                        validate: (val: string) => {
                          if (watch('password') !== val) {
                            return 'Your passwords do no match';
                          }
                        },
                      }
                    : {}
                )}
                error={errors.password_confirmation?.message}
              />
              <img
                onClick={() => setShowPassword1((pr) => !pr)}
                className={styles.eyeIcon}
                src={showPassword1 ? EyeIcon : EyeDisabledIcon}
                alt="eye"
              />
            </div>
          </FormGroup>
          {isStaff ? (
            <FormGroup columns={2}>
              <Controller
                name="role"
                control={control}
                defaultValue={
                  teamId !== undefined
                    ? {
                        value: 'driver',
                        label: 'Driver (driver app)',
                      }
                    : undefined
                }
                rules={{required: 'Required'}}
                render={({field: {ref, ...field}}) => (
                  <div style={{position: 'relative'}}>
                    <div
                      style={{
                        marginBottom: 6,
                        cursor: 'pointer',
                        marginTop: 22,
                      }}
                    >
                      Staff Type
                    </div>
                    <SelectField
                      isDisabled={teamId !== undefined}
                      placeholder="Staff Type"
                      options={staffTypeOptions}
                      error={errors?.role?.message}
                      {...field}
                    />
                    {errors?.role?.message && (
                      <Typography className={styles.error} variant="p">
                        {errors?.role?.message}
                      </Typography>
                    )}
                  </div>
                )}
              />
            </FormGroup>
          ) : (
            <FormGroup columns={2}>
              <Controller
                control={control}
                name="field_role"
                render={({field}) => {
                  return (
                    <CreatableSelect
                      label="Role"
                      isDisabled={creatingFieldRole}
                      isLoading={creatingFieldRole}
                      placeholder="Search For Role"
                      components={{
                        DropdownIndicator: () => null,
                      }}
                      error={errors?.field_role?.message}
                      options={fieldRoleOptions}
                      onCreateOption={onCreateFieldRoleOption}
                      value={field.value as any}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  );
                }}
              />
            </FormGroup>
          )}
        </CardContent>
      </ListWrapper>
      {!!isModal && (
        <FormActionGroup style={{justifyContent: 'flex-end', padding: 20}}>
          {!!employeeId && canDelete && (
            <Button
              onClick={() => handleDeleteEmployee(+employeeId)}
              color="danger"
              loading={isDeleting}
            >
              Delete
            </Button>
          )}
          {canUpdate && (
            <Button onClick={handleSubmit} loading={submitting}>
              Save
            </Button>
          )}
        </FormActionGroup>
      )}

      {currentUser?.role === 'management' && employeeId && isStaff && (
        <>
          <Typography className={styles.header} variant="h2">
            Permissions
          </Typography>
          <ListWrapper className={styles.permissionContainer}>
            <CardContent px="none" pt="none" pb="none">
              <div className={styles.permissionHeader}>
                <div className={styles.permissionHeaderItem}>
                  <Typography variant="p"></Typography>
                </div>
                <div className={styles.permissionHeaderItem}>
                  <Typography variant="p">View</Typography>
                </div>
                <div className={styles.permissionHeaderItem}>
                  <Typography variant="p">Edit</Typography>
                </div>
                <div className={styles.permissionHeaderItem}>
                  <Typography variant="p">Delete</Typography>
                </div>
              </div>
              {permissions.map((item) => {
                return (
                  <div className={styles.permissionHeader} key={item.id}>
                    <div className={styles.permissionHeaderItem}>
                      <Typography variant="p">{item.name}</Typography>
                    </div>
                    <div className={styles.permissionHeaderItem}>
                      <Checkbox
                        className={styles.checkbox}
                        onChange={(e) => {
                          onChangePermission(item.id, 'read');
                        }}
                        checked={get(
                          userPermissions,
                          `${item.id}.permissions.read`,
                          false
                        )}
                      />
                    </div>
                    <div className={styles.permissionHeaderItem}>
                      <Checkbox
                        className={styles.checkbox}
                        onChange={(e) => onChangePermission(item.id, 'write')}
                        checked={get(
                          userPermissions,
                          `${item.id}.permissions.write`,
                          false
                        )}
                      />
                    </div>
                    <div className={styles.permissionHeaderItem}>
                      <Checkbox
                        className={styles.checkbox}
                        onChange={(e) => onChangePermission(item.id, 'delete')}
                        checked={get(
                          userPermissions,
                          `${item.id}.permissions.delete`,
                          false
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </ListWrapper>
        </>
      )}

      <Modal
        isOpen={modalVisible}
        style={{
          content: {
            maxHeight: '90vh',
            overflow: 'scroll',
            minWidth: 800,
          },
        }}
        onClose={() => setModalVisible(false)}
        onRequestClose={() => setModalVisible(false)}
      >
        <VehicleForm
          setModalVisible={setModalVisible}
          setUser={(data) => {
            setTeam(data);
            setModalVisible(false);
          }}
          isModal={true}
        />
      </Modal>
      <DeleteModal
        openModal={deleteModal}
        setOpenModal={setDeleteModal}
        onDelete={() => handleDeleteEmployee(+employeeId)}
        text="Are you sure, you want to delete?"
      />
    </div>
  );
}

export default EmployeeForm;
